import { LineItem } from 'types/parts-diagram';
import PartsOverviewCard from './parts-overview-card/parts-overview-card';
import PartsOverviewMobileCard from './parts-overview-mobile-card/parts-overview-mobile-card';
import { FC, Suspense, useState } from 'react';
import { itemSummaryApiCall } from 'services/parts-diagram/actions';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import {
  GA_PDP_DIAGRAM,
  PARTS_DIAGRAM_PAGE
} from 'constants/analyticsConstants';
import ProductQuickViewDrawer from 'components/product-quick-view-drawer';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';

export interface PartsOverviewCardWrapperProps {
  currency: string;
  partItem: LineItem;
  cartId: string;
  index: number;
  handlePartView: (lineItem: LineItem) => void;
  onHover: (referenceNumber: string | null) => void;
  selectedItem: string | null;
  shouldHideBtn?: boolean;
  lastItem: number;
  partsDiagramMobileView?: boolean;
  isPccPdpHeaderUpdateMobile?: boolean;
  isSerialCompatible?: boolean;
  fromProductPage?: boolean;
  contactDealerBtn?: React.ReactNode;
  allCardsHasContactDealer?: boolean;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
  hoveredItem?: string | null;
  isZoomImageEnhancementFlag?: boolean;
  isHeadlessPriceAvailability_additional_sec?: boolean;
  isHighlighted?: boolean;
  onPartClick?: (partId: string) => void;
  clickedOnPartId?: string;
}

const PartsOverviewCardWrapper: FC<PartsOverviewCardWrapperProps> = ({
  currency,
  partItem,
  cartId,
  handlePartView,
  onHover,
  selectedItem,
  index,
  shouldHideBtn,
  lastItem,
  partsDiagramMobileView,
  isSerialCompatible,
  fromProductPage,
  contactDealerBtn,
  allCardsHasContactDealer,
  isPccPdpHeaderUpdateMobile,
  isRTL = false,
  currencyLdFeatureFlag = false,
  hoveredItem,
  isZoomImageEnhancementFlag = false,
  isHeadlessPriceAvailability_additional_sec = false,
  isHighlighted,
  onPartClick,
  clickedOnPartId
}) => {
  const { partNumber = '' } = partItem || {};
  const [drawerActive, setDrawerActive] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const [itemSummaryCardData, setItemSummaryCardData] = useState(null);

  const { isCatCorp, analyticsCommonInfo: { dealerData, model } = {} } =
    usePartDiagram();

  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();

  const handleOpen = async (
    partNumber: string,
    price: ProductDealerPriceAvailability
  ) => {
    fireSelectItemEvent({
      item: {
        orderItemId: cartId,
        partNumber,
        quantity: Number(partItem.quantity),
        availabilityMessage: price
          ? price.availability?.[0]?.availabilityMessage
          : '',
        totalPrice: price
          ? price.unformattedTotalPrice && Number(price.unformattedTotalPrice)
          : 0
      },
      partName: partItem && `${partItem?.partNumber}: ${partItem?.name}`,
      locationId: '',
      itemSummary: {
        categoryPath: partItem?.priceAndAvailabilityData?.categoryPath,
        manufacturer: ''
      },
      itemListId: GA_PDP_DIAGRAM,
      itemListName: GA_PDP_DIAGRAM,
      index: parseInt(partItem?.referenceNumber),
      isSearchPageEvent: true
    });

    !isCatCorp && setPriceData(price);
    const itemSummaryResponse = await itemSummaryApiCall(partNumber);
    setItemSummaryCardData(itemSummaryResponse);
  };

  return (
    <>
      {!partsDiagramMobileView ? (
        <PartsOverviewCard
          currency={currency}
          partItem={partItem}
          key={partNumber}
          handlePartView={handlePartView}
          onHover={onHover}
          index={index}
          shouldHideBtn={shouldHideBtn}
          lastItem={lastItem}
          handleOpen={handleOpen}
          openQuickViewDrawer={setDrawerActive}
          fromProductPage={fromProductPage}
          isRTL={isRTL}
          currencyLdFeatureFlag={currencyLdFeatureFlag}
          hoveredItem={hoveredItem}
          isZoomImageEnhancementFlag={isZoomImageEnhancementFlag}
          isHeadlessPriceAvailability_additional_sec={
            isHeadlessPriceAvailability_additional_sec
          }
          isHighlighted={isHighlighted}
          onPartClick={onPartClick}
          clickedOnPartId={clickedOnPartId}
        />
      ) : (
        <>
          <div className="d-none d-md-block">
            <PartsOverviewCard
              currency={currency}
              partItem={partItem}
              key={partNumber}
              handlePartView={handlePartView}
              onHover={onHover}
              index={index}
              shouldHideBtn={shouldHideBtn}
              lastItem={lastItem}
              handleOpen={handleOpen}
              openQuickViewDrawer={setDrawerActive}
              fromProductPage={fromProductPage}
              isRTL={isRTL}
              currencyLdFeatureFlag={currencyLdFeatureFlag}
              hoveredItem={hoveredItem}
              isHighlighted={isHighlighted}
              onPartClick={onPartClick}
              clickedOnPartId={clickedOnPartId}
            />
          </div>
          <div className="d-md-none">
            <PartsOverviewMobileCard
              partItem={partItem}
              selectedItem={selectedItem}
              onSelectItem={onHover}
              key={partNumber + '-mobile'}
              handleOpen={handleOpen}
              isPccPdpHeaderUpdateMobile={isPccPdpHeaderUpdateMobile}
              openQuickViewDrawer={setDrawerActive}
              isSerialCompatible={isSerialCompatible}
              fromProductPage={fromProductPage}
              contactDealerBtn={contactDealerBtn}
              allCardsHasContactDealer={allCardsHasContactDealer}
              isImageShown={true}
              partsDiagramMobileView={partsDiagramMobileView}
              currencyNotation={currency}
              isRTL={isRTL}
              currencyLdFeatureFlag={currencyLdFeatureFlag}
            />
          </div>
        </>
      )}
      {itemSummaryCardData && (
        <Suspense fallback={null}>
          <ProductQuickViewDrawer
            data={itemSummaryCardData}
            priceData={priceData}
            dealerData={dealerData}
            currency={currency}
            model={model}
            listEvent={PARTS_DIAGRAM_PAGE}
            drawerActive={drawerActive}
            setDrawerActive={setDrawerActive}
            isPccPdpHeaderUpdateMobile={isPccPdpHeaderUpdateMobile}
            isRTL={isRTL}
            currencyLdFeatureFlag={currencyLdFeatureFlag}
            isPartDiagram={true}
          />
        </Suspense>
      )}
    </>
  );
};

export default PartsOverviewCardWrapper;
